import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { ReactComponent as AssessmentTimerIcon } from 'assets/images/assessments/AssessmentTimer.svg';
import { Button } from 'components/Button/Button';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { formatCountdown } from 'utils/dateUtils';

interface AssessmentTimeoutDialogProps {
  open: boolean;
  initialSeconds?: number;
  onContinue: () => void;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    width: '50vw',
    height: '50vh',
    minWidth: 320,
    minHeight: 480,
    overflow: 'hidden',
  },
});

const StyledDialogHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5),
  borderBottom: `1.5px solid ${theme.palette.divider}`,
}));

const StyledDialogTitle = styled(DialogTitle)({
  padding: 0,
  textAlign: 'center',
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '75%',
  margin: '24px auto 0 auto',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3, 0, 3),
  textAlign: 'center',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3, 3, 3, 3),
}));

const StyledButton = styled(Button)({
  width: '100%',
  height: 51,
  borderRadius: 50,
});

export const AssessmentTimeoutDialog = ({
  open,
  initialSeconds = 300,
  onContinue,
  onClose,
}: AssessmentTimeoutDialogProps) => {
  const { isAlertOpen } = useLayoutElements();
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) {
      setSecondsLeft(initialSeconds);
      return undefined;
    }

    setSecondsLeft(initialSeconds);

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onCloseRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, initialSeconds]);

  return (
    <StyledDialog
      open={open}
      onClose={onContinue}
      fullWidth
      maxWidth="xs"
      aria-labelledby="assessment-timeout-dialog__title"
      aria-describedby="assessment-timeout-dialog__body"
      disableEscapeKeyDown={isAlertOpen}
      disableEnforceFocus
      PaperProps={{ tabIndex: 0 }}
    >
      <StyledDialogHeader>
        <StyledDialogTitle>Session Timeout</StyledDialogTitle>

        <IconButton
          id="assessment-timeout-dialog__close-button"
          onClick={onContinue}
          aria-label="Close dialog"
          size="small"
          sx={{
            position: 'absolute',
            right: 12,
          }}
        >
          <CloseRounded />
        </IconButton>
      </StyledDialogHeader>
      <StyledDialogContent>
        <AssessmentTimerIcon aria-hidden="true" />
        <Stack gap={2}>
          <Typography variant="body1" id="assessment-timeout-dialog__body">
            Your session will expire in{' '}
            <Typography component="span" variant="body1" fontWeight="bold">
              {formatCountdown(secondsLeft)}
            </Typography>
            . Select Continue to stay, or Close Session to end your session now.
          </Typography>
          <Typography variant="body1">
            If you do nothing, your session will expire automatically.
          </Typography>
        </Stack>
      </StyledDialogContent>
      <StyledDialogActions disableSpacing>
        <StyledButton
          id="assessment-timeout-dialog__continue-button"
          variant="primary"
          onClick={onContinue}
        >
          Continue
        </StyledButton>
        <StyledButton
          id="assessment-timeout-dialog__close-session-button"
          variant="outlined"
          onClick={onClose}
        >
          Close Session
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};
